import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import api from '../../services/api';
import styles from './BookDetail.module.css';
import LoadingSpinner from '../../components/LoadingSpinner';
import { HeartIcon, LoaderIcon } from '../../components/Icons';
import { resolveCoverUrl } from '../../services/api';

const BookDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const bookId = searchParams.get('bookid');
  const fromPathRef = useRef(location.state?.from || '/discover');
  const fetchedBookIdRef = useRef(null); // 避免开发环境 StrictMode 触发重复请求
  const [book, setBook] = useState(null);
  const [volumes, setVolumes] = useState([]);
  const [volumeChapters, setVolumeChapters] = useState({});
  const [chaptersLoadingId, setChaptersLoadingId] = useState(null);
  const [activeVolumeId, setActiveVolumeId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [price, setPrice] = useState(null);
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [favoriteBookId, setFavoriteBookId] = useState(null);
  const [favoriteLoading, setFavoriteLoading] = useState(false);
  const [completedChapterIds, setCompletedChapterIds] = useState(new Set());

  // 当路由变化时（包括前进/后退），重置 fetchedBookIdRef
  useEffect(() => {
    fetchedBookIdRef.current = null;
  }, [location.key]);

  useEffect(() => {
    // React 18 StrictMode 在开发环境会双调用 effect，这里用 ref 保证同一本书只拉取一次
    if (!bookId || fetchedBookIdRef.current === bookId) return;
    fetchedBookIdRef.current = bookId;

    fetchBookDetail();
    fetchBookVolumes();
    checkFavoriteStatus();
    
    // 组件卸载时重置
    return () => {
      fetchedBookIdRef.current = null;
    };
  }, [bookId]);

  const fetchBookDetail = async () => {
    try {
      const response = await api.get(`/novels/${bookId}`);
      setBook(response.data);
    } catch (error) {
      console.error('获取书籍详情失败:', error);
      setError('获取书籍详情失败');
    }
  };

  const fetchBookVolumes = async () => {
    try {
      setLoading(true);
      // 获取所有卷的元数据，但不预取章节，避免一次性加载大量数据
      const response = await api.get(`/volumes/?novel_id=${bookId}&include_chapters=false`);
      const volumesData = response.data;
      setVolumes(volumesData);
      
      // 设置第一个卷为默认激活状态
      if (volumesData.length > 0) {
        const firstId = volumesData[0].id;
        setActiveVolumeId(firstId);
        await fetchChaptersForVolume(firstId);
      }
    } catch (error) {
      console.error('获取卷列表失败:', error);
      setError('获取卷列表失败');
    } finally {
      setLoading(false);
    }
  };

  const fetchChaptersForVolume = async (volumeId) => {
    if (!volumeId || volumeChapters[volumeId]) return;
    try {
      setChaptersLoadingId(volumeId);
      const res = await api.get(`/volumes/${volumeId}/chapters`);
      setVolumeChapters((prev) => ({
        ...prev,
        [volumeId]: Array.isArray(res.data) ? res.data : [],
      }));
    } catch (err) {
      console.error('获取章节列表失败:', err);
      alert('章节加载失败，请稍后重试');
    } finally {
      setChaptersLoadingId(null);
    }
  };

  const handleVolumeSelect = async (volumeId) => {
    setActiveVolumeId(volumeId);
    await fetchChaptersForVolume(volumeId);
    await fetchCompletedChapters(volumeId);
  };

  useEffect(() => {
    if (!bookId || !activeVolumeId) return;
    fetchCompletedChapters(activeVolumeId);
  }, [bookId, activeVolumeId]);

  const fetchCompletedChapters = async (volumeId) => {
    try {
      const params = new URLSearchParams();
      params.append('novel_id', bookId);
      if (volumeId) {
        params.append('volume_id', volumeId);
      }
      const response = await api.get(`/completed-chapters/?${params.toString()}`);
      const entries = Array.isArray(response.data) ? response.data : [];
      const ids = entries.map((entry) => entry.chapter_id);
      setCompletedChapterIds(new Set(ids));
    } catch (error) {
      if (error?.response?.status !== 401) {
        console.error('获取完成章节状态失败:', error);
      }
      setCompletedChapterIds(new Set());
    }
  };

  const handleBack = () => {
    const target = fromPathRef.current || '/discover';
    if (location.state?.historyReturn) {
      const historyFrom = location.state?.from || target;
      navigate(target, {
        state: {
          historyReturn: location.state.historyReturn,
          from: historyFrom,
        },
      });
      return;
    }
    navigate(target);
  };

  const checkFavoriteStatus = async () => {
    try {
      const response = await api.get(`/favorite-books/check/${bookId}`);
      if (response.data?.is_favorited) {
        setFavoriteBookId(response.data.favorite_id);
      } else {
        setFavoriteBookId(null);
      }
    } catch (error) {
      console.error('检查收藏状态失败:', error);
    }
  };

  const handleFavoriteToggle = async () => {
    if (!bookId) return;
    setFavoriteLoading(true);
    try {
      if (favoriteBookId) {
        await api.delete(`/favorite-books/${favoriteBookId}`);
        setFavoriteBookId(null);
      } else {
        const response = await api.post('/favorite-books/', { novel_id: Number(bookId) });
        setFavoriteBookId(response.data?.id ?? null);
      }
    } catch (error) {
      console.error('更新收藏状态失败:', error);
      if (error?.response?.status === 401) {
        alert('请先登录后再收藏');
      } else {
        alert('收藏操作失败，请稍后重试');
      }
    } finally {
      setFavoriteLoading(false);
    }
  };

  const handleChapterClick = async (chapter) => {
    console.log('点击章节:', chapter.title);

    navigate(`/read?bookid=${bookId}&chapterid=${chapter.id}`, {
      state: {
        from: `/book?bookid=${bookId}`,
        historyReturn: location.state?.historyReturn || location.state?.from || null,
      },
    });
  };

  const getActiveVolume = () => {
    return volumes.find(volume => volume.id === activeVolumeId);
  };

  const activeChapters = volumeChapters[activeVolumeId] || [];
  const totalChapters = volumes.reduce(
    (sum, vol) => sum + (volumeChapters[vol.id]?.length ?? vol.chapters?.length ?? 0),
    0
  );

  if (loading) {
    return (
      <div className={styles.loading}>
        <LoadingSpinner size="large" text="加载中..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.error}>
        <p>{error}</p>
        <button onClick={handleBack} type="button" className={styles.backBtn} aria-label="返回">
          <span className={`material-symbols-outlined ${styles.backIcon}`}>arrow_back</span>
        </button>
      </div>
    );
  }

  if (!book) {
    return (
      <div className={styles.error}>
        <p>书籍不存在</p>
        <button onClick={handleBack} type="button" className={styles.backBtn} aria-label="返回">
          <span className={`material-symbols-outlined ${styles.backIcon}`}>arrow_back</span>
        </button>
      </div>
    );
  }

  return (
    <div className={styles.bookDetail}>
      {/* 返回按钮 */}
      <div className={styles.header}>
        <button onClick={handleBack} type="button" className={styles.backBtn} aria-label="返回">
          <span className={`material-symbols-outlined ${styles.backIcon}`}>arrow_back</span>
        </button>
      </div>

      {/* 封面 + 基本信息 + 简介 */}
      <div className={styles.bookTopSection}>
        <div className={styles.bookCover}>
          {book.cover_image_url ? (
            <img
              src={resolveCoverUrl(book.cover_image_url)}
              alt={book.title}
              loading="lazy"
            />
          ) : (
            <div className={styles.coverPlaceholder}>暂无封面</div>
          )}
        </div>

        <div className={styles.bookInfoStack}>
          <div className={styles.bookMeta}>
            <div className={styles.bookTitleRow}>
              <div className={styles.titleWithAction}>
                <h1 className={styles.bookTitle}>{book.title}</h1>
                <button
                  type="button"
                  className={`${styles.favoriteBtn} ${favoriteBookId ? styles.favoriteBtnActive : ''}`}
                  onClick={handleFavoriteToggle}
                  disabled={favoriteLoading}
                  title={favoriteBookId ? '取消收藏' : '收藏此书'}
                  aria-label={favoriteBookId ? '取消收藏' : '收藏此书'}
                >
                  {favoriteLoading ? (
                    <LoaderIcon size={16} />
                  ) : (
                    <HeartIcon size={22} filled={!!favoriteBookId} />
                  )}
                </button>
              </div>
            </div>
            <p className={styles.bookAuthor}>作者：{book.author}</p>

            <div className={styles.bookStats}>
              <span className={styles.statItem}>
                状态：{book.is_completed ? '已完结' : '连载中'}
              </span>
              <span className={styles.statItem}>
                卷数：{volumes.length}
              </span>
              <span className={styles.statItem}>
                总章节：{totalChapters}
              </span>
            </div>
          </div>

          {/* 书籍简介 */}
          {book.description && (
            <div className={styles.description}>
              <div
                className={`${styles.descriptionContent} ${
                  showFullDescription ? styles.descriptionExpandedContent : styles.descriptionCollapsedContent
                }`}
              >
                <p
                  className={`${styles.descriptionText} ${
                    showFullDescription ? styles.descriptionExpanded : styles.descriptionCollapsed
                  }`}
                >
                  {book.description}
                </p>
              </div>
              <button
                type="button"
                className={`${styles.descriptionToggle} ${
                  showFullDescription ? styles.toggleExpanded : styles.toggleCollapsed
                }`}
                onClick={() => setShowFullDescription(prev => !prev)}
              >
                {showFullDescription ? '收起简介' : '展开更多'}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* 卷和章节列表 */}
      <div className={styles.volumesSection}>
        <h3 className={styles.sectionTitle}>章节目录</h3>
        
        {volumes.length === 0 ? (
          <div className={styles.noVolumes}>
            <p>暂无章节内容</p>
          </div>
        ) : (
          <>
            {/* 卷标签 */}
            <div className={styles.volumeTabs}>
              {volumes.map(volume => (
                <button
                  key={volume.id}
                  className={`${styles.volumeTab} ${
                    activeVolumeId === volume.id ? styles.active : ''
                  }`}
                  onClick={() => handleVolumeSelect(volume.id)}
                >
                  {volume.title}
                  <span className={styles.chapterCount}>
                    ({(volumeChapters[volume.id]?.length ?? 0)}章)
                  </span>
                </button>
              ))}
            </div>

            {/* 章节列表 */}
            <div className={styles.chapterList}>
              {chaptersLoadingId === activeVolumeId ? (
                <div className={styles.noChapters}>
                  <p>章节加载中...</p>
                </div>
              ) : activeChapters.length > 0 ? (
                activeChapters
                  .slice()
                  .sort((a, b) => a.order_index - b.order_index)
                  .map(chapter => (
                    <div
                      key={chapter.id}
                      className={styles.chapterItem}
                      onClick={() => handleChapterClick(chapter)}
                    >
                      <div className={styles.chapterText}>
                        <span className={styles.chapterTitle}>{chapter.title}</span>
                        <span className={styles.chapterIndex}>第{chapter.order_index}章</span>
                      </div>
                      <div className={styles.chapterItemMeta}>
                        {completedChapterIds.has(chapter.id) && (
                          <span className={styles.chapterStatus}>
                            <span className={styles.chapterStatusDot}>•</span>
                            <span className={styles.chapterStatusText}>已读</span>
                          </span>
                        )}
                      </div>
                    </div>
                  ))
              ) : (
                <div className={styles.noChapters}>
                  <p>该卷暂无章节</p>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default BookDetail;
